import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageShell } from '../../shared/components/page-shell/page-shell';
import { SubscriptionsTab } from './components/subscriptions-tab/subscriptions-tab';
import { ExecutablesTab } from './components/executables-tab/executables-tab';
import { ImagingTab } from './components/imaging-tab/imaging-tab';
import { ClinicTab } from './components/clinic-tab/clinic-tab';
import { BillingTab } from './components/billing-tab/billing-tab';
import { PatientFieldsTab } from './components/patient-fields-tab/patient-fields-tab';
import { NovigestTab } from './components/novigest-tab/novigest-tab';
import { AgendaTab } from './components/agenda-tab/agenda-tab';
import { AccessProfilesTab } from './components/access-profiles-tab/access-profiles-tab';
import { UsersTab } from './components/users-tab/users-tab';
import { DiagnosticTreatmentTab } from './components/diagnostic-treatment-tab/diagnostic-treatment-tab';
import { SpecialityTab } from './components/speciality-tab/speciality-tab';
import { BookingSettingsTab } from './components/booking-settings-tab/booking-settings-tab';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppTabs } from '../../shared/components/app-tabs/app-tabs';

import { ProfileTab } from './components/profile-tab/profile-tab';
// import { SecurityTab } from './components/security-tab/security-tab';
import { AuditLogsPage } from '../audit-logs/audit-logs';

import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, switchMap, map, startWith } from 'rxjs';
import { selectPermissions, selectUserInfo, selectBranchId } from '../../core/store/auth/auth.selectors';
import { BookingSettingsService } from '../../core/services/booking-settings.service';
import { ClinicPlanService } from '../../core/services/clinic-plan.service';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PageShell,
    SubscriptionsTab,
    ExecutablesTab,
    ImagingTab,
    ClinicTab,
    BillingTab,
    PatientFieldsTab,
    NovigestTab,
    AgendaTab,
    AccessProfilesTab,
    UsersTab,
    DiagnosticTreatmentTab,
    AppTabs,
    SpecialityTab,
    ProfileTab,
    AuditLogsPage,
    BookingSettingsTab,
  ],
  templateUrl: './settings.html',
})
export class Settings implements OnInit {
  activeTab = 'profile';
  private readonly plans = inject(ClinicPlanService);
  private readonly plan$ = toObservable(this.plans.plan);

  tabs$!: Observable<any[]>;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private store: Store,
    private bookingSettingsService: BookingSettingsService
  ) {}

  private readonly baseTabs: any[] = [
    { value: 'profile', label: 'settings_tab_profile' },
    { value: 'subscriptions', label: 'settings_tab_subscriptions' },
    { value: 'executables', label: 'settings_tab_executables' },
    { value: 'imaging', label: 'settings_tab_imaging' },
    { value: 'clinic', label: 'settings_tab_clinic' },
    { value: 'billing', label: 'settings_tab_billing' },
    { value: 'patient_fields', label: 'settings_tab_patient_fields' },
    { value: 'novigest', label: 'settings_tab_novigest' },
    { value: 'agenda', label: 'settings_tab_agenda' },
    { value: 'access_profiles', label: 'settings_tab_access_profiles' },
    { value: 'users', label: 'settings_tab_users' },
    { value: 'diagnostics_treatments', label: 'settings_tab_diagnostics_treatments' },
    { value: 'specialty', label: 'speciality' },
    // { value: 'security', label: 'settings_tab_security' },
  ];

  ngOnInit() {
    // Audit logs is a superadmin (user_type_id === 2) feature gated by the
    // 'Auditoria' permission — appended as the last tab, mirroring the old sidebar rule.
    // Online booking is gated by the branch's clinic_subscriptions status (trial/active).
    // Both conditional tabs are resolved together in one reactive pipeline.
    // Reactive (no take(1)) so it appears once the auth state hydrates.
    //
    // Bound via the async pipe in the template instead of manually writing to
    // a plain `tabs` field: NgRx selectors replay synchronously on subscribe,
    // so a manual write here can land a second value inside the SAME
    // change-detection pass that already rendered the first one, which is
    // exactly what NG0100 (ExpressionChangedAfterItHasBeenCheckedError) is —
    // a setTimeout hack around that is timing-dependent and still flaky.
    // AsyncPipe schedules its updates through markForCheck correctly, so this
    // whole class of bug doesn't come up.
    this.tabs$ = combineLatest([
      this.store.select(selectPermissions),
      this.store.select(selectUserInfo),
      this.store.select(selectBranchId),
      this.plan$,
    ]).pipe(
      switchMap(([perms, userInfo, branchId]: [any, any, number | null, unknown]) =>
        (branchId ? this.bookingSettingsService.hasCrmAccess(branchId) : of(false)).pipe(
          map((hasCrmAccess) => ({ perms, userInfo, hasCrmAccess }))
        )
      ),
      map(({ userInfo, hasCrmAccess }) => {
        // A lapsed plan leaves the owner their profile and the plan itself.
        if (this.plans.blocked()) {
          if (this.activeTab !== 'profile') this.activeTab = 'subscriptions';
          return this.baseTabs.filter((t) => t.value === 'profile' || t.value === 'subscriptions');
        }
        const canAudit = Number(userInfo?.user_type_id) === 2;
        let tabs = [...this.baseTabs];
        if (hasCrmAccess) {
          tabs = [...tabs, { value: 'online_booking', label: 'settings_tab_online_booking' }];
        }
        if (canAudit) {
          tabs = [...tabs, { value: 'audit_logs', label: 'settings_tab_audit_logs' }];
        }
        return tabs;
      }),
      startWith(this.baseTabs)
    );

    this.route.queryParams.subscribe((params) => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
    });
  }

  setActiveTab(tabId: string) {
    console.log('Setting active tab:', tabId);
    this.activeTab = tabId;
  }
}
